import {
  format as dateFnsFormat,
  addMinutes,
  addHours,
  addDays,
  addWeeks,
  addMonths,
  addYears,
  subMinutes,
  subHours,
  subDays,
  subWeeks,
  subMonths,
  subYears,
  startOfDay,
  endOfDay,
  isBefore as _isBefore,
  isAfter as _isAfter,
  isWithinInterval,
  isEqual as _isEqual,
  getMinutes,
  getDay,
  getDate as _getDate,
  setMinutes,
  setHours,
  setSeconds,
  getHours,
  getMonth,
  getYear,
  setMonth,
  setYear,
  getSeconds,
  setDate,
  getWeek,
  getISODay,
  setISODay,
  setISOWeek,
  getISOWeek,
  addSeconds,
  subSeconds,
  startOfWeek,
  startOfMonth,
  startOfYear,
  startOfMinute,
  startOfHour,
  endOfHour,
  endOfMinute,
  endOfMonth,
  endOfWeek,
  endOfYear,
  differenceInSeconds,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  setMilliseconds,
  getWeekOfMonth,
  formatISO,
  getDayOfYear,
  isEqual,
  addMilliseconds,
  subMilliseconds,
  parse as _parse,
  format,
} from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import moment from 'moment-timezone'

export type DateT = Date | number | string

export enum TIME {
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  MONTHS = 'months',
  YEARS = 'years',
  WEEKS = 'weeks',
  MILLISECONDS = 'milliseconds',
}

export enum Action {
  ADD = 'add',
  SUBTRACT = 'subtract',
}

interface DateUtil {
  isDateUtil: boolean
  iso(param: number, interval: TIME): DateUtil
  getIso(interval: TIME): number
  tz(timezone?: string): DateUtil
  getTz(): string
  utc(): DateUtil
  locale(param: string): DateUtil
  toDate(): Date
  seconds(): number
  minutes(): number
  date(): number
  hours(): number
  day(): number
  week(): number
  month(): number
  year(): number
  set(param: {
    millisecond?: number
    minute?: number
    second?: number
    hour?: number
    date?: number
    month?: number
    year?: number
  }): DateUtil
  setDate(date: DUDateT): DateUtil
  weekDay(index: number): DateUtil
  manupulateDate(value: number, time: TIME, action: Action): DateUtil
  add(value: number, time: TIME): DateUtil
  sub(value: number, time: TIME): DateUtil
  format(formatString: string): string
  parse(timeString: string): string
  fullDateFormat(): string
  fullDateWithTimeFormat(): string
  timeFormat(): string
  zoneAbbr(): string
  isBefore(dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isSameOrBefore(dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isAfter(dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isSameOrAfter(dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isSame(dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isNoon(): boolean
  isMidNight(): boolean
  startOf(interval: TIME): DateUtil
  endOf(interval: TIME): DateUtil
  isBetween(start: Date | DateUtil, end: Date | DateUtil): boolean
  getDatesInRange(endDate: DateUtil): DateUtil[]
  diff(endDate: DUDateT, interval: TIME): number
  clone(): DateUtil
  reset(interval: TIME): DateUtil
  toISOString(): string
  toString(): string
  startOfDayToDate(): DateUtil
  endOfDayToDate(): DateUtil
  isBeforeToDate(dateToCompare: string | Date | number): boolean
  isInTheRange(dateInterval: IntervalT): boolean
  isEqualToDate(dateToCompare: string | Date | number): boolean
}
// DateUtilDate Type
type DUDateT = DateT | DateUtil
type DateUtilT = (date?: DUDateT, timezone?: string) => DateUtil

type DateUtilTStatic = {
  tz(...args: unknown[]): DateUtil
  utc(date: DateT, timezone?: string): DateUtil
  isDateUtil(date: DUDateT): boolean
  isSame(dateObj: DUDateT, dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isBefore(dateObj: DUDateT, dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isSameOrBefore(dateObj: DUDateT, dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isAfter(dateObj: DUDateT, dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  isSameOrAfter(dateObj: DUDateT, dateToCompare: DUDateT, interval?: TIME | TIME[]): boolean
  startOf(dateObj: Date, interval: TIME): DateUtil
  endOf(dateObj: Date, interval: TIME): DateUtil
  getDatesInRange(startDay: DateUtil, endDate: DateUtil): DateUtil[]
  diff(startDate: DUDateT, endDate: DUDateT, interval: TIME): number
  parse(dateString: string, format?: string): DateUtil
  defaultTz(): string
}

type IntervalT = {
  start: Date
  end: Date
}

type ActionMapT = {
  [key in TIME]: {
    [key in Action]: (date: number | Date, value: number) => Date
  }
}

const actionMap: ActionMapT = {
  [TIME.SECONDS]: {
    [Action.ADD]: addSeconds,
    [Action.SUBTRACT]: subSeconds,
  },
  [TIME.MINUTES]: {
    [Action.ADD]: addMinutes,
    [Action.SUBTRACT]: subMinutes,
  },
  [TIME.HOURS]: {
    [Action.ADD]: addHours,
    [Action.SUBTRACT]: subHours,
  },
  [TIME.DAYS]: {
    [Action.ADD]: addDays,
    [Action.SUBTRACT]: subDays,
  },
  [TIME.WEEKS]: {
    [Action.ADD]: addWeeks,
    [Action.SUBTRACT]: subWeeks,
  },
  [TIME.MONTHS]: {
    [Action.ADD]: addMonths,
    [Action.SUBTRACT]: subMonths,
  },
  [TIME.YEARS]: {
    [Action.ADD]: addYears,
    [Action.SUBTRACT]: subYears,
  },
  [TIME.MILLISECONDS]: {
    [Action.ADD]: addMilliseconds,
    [Action.SUBTRACT]: subMilliseconds,
  },
}

const calculateOffset = (timezone: string) =>
  Math.round(DateUtil().diff(DateUtil.tz(timezone), TIME.MINUTES) / 10) * 10

const DateUtil: DateUtilT & DateUtilTStatic = (date, timezone) => {
  if (isDateUtil(date || new Date())) return date as DateUtil
  let dateObj: Date = new Date()
  let timeZone = timezone || DateUtil.defaultTz()
  let locale: { locale?: Locale | undefined }
  // let offset = dateObj.getTimezoneOffset()

  if (typeof date === 'string') dateObj = new Date(date)
  if (date instanceof Date) dateObj = date

  const tempDate = zonedTimeToUtc(dateObj, timeZone)
  dateObj = utcToZonedTime(tempDate, timeZone)

  const dateUtilObj: DateUtil = {
    isBeforeToDate(dateToCompare: string | number | Date) {
      dateToCompare = typeof dateToCompare === 'string' ? new Date(dateToCompare) : dateToCompare
      return isBefore(dateObj, dateToCompare)
    },
    isEqualToDate(dateToCompare: string | number | Date) {
      dateToCompare = typeof dateToCompare === 'string' ? new Date(dateToCompare) : dateToCompare
      return _isEqual(dateObj, dateToCompare)
    },
    isDateUtil: true,
    toDate() {
      return dateObj
    },
    tz(timezone) {
      if (timezone && timezone !== timeZone) {
        const date = zonedTimeToUtc(dateObj, timeZone)
        timeZone = timezone
        dateObj = utcToZonedTime(date, timezone)
      }
      return dateUtilObj
    },
    getTz() {
      return timeZone
    },
    utc() {
      return dateUtilObj
        .clone()
        .add(dateObj.getTimezoneOffset() + calculateOffset(timeZone), TIME.MINUTES)
    },
    locale(param: string) {
      locale = { locale: { code: param } }
      return dateUtilObj
    },
    format(formatString) {
      return dateFnsFormat(
        dateObj,
        formatString.replaceAll('Y', 'y').replaceAll('D', 'd').replace('A', 'a'),
        locale
      )
    },
    parse(timeString) {
      const parsedTime = _parse(timeString, 'h:mm aa', new Date())
      return format(parsedTime, 'HH:mm')
    },
    fullDateFormat() {
      return this.format('MMMM d, YYY')
    },
    fullDateWithTimeFormat() {
      return this.format('yyyy-MM-dd HH:mm:ss')
    },
    timeFormat() {
      return this.format('h.mm aa')
    },
    zoneAbbr() {
      return this.format('z')
    },
    isBefore(dateToCompare, interval?) {
      return isBefore(dateObj, dateToCompare, interval)
    },
    isAfter(dateToCompare, interval?) {
      return isAfter(dateObj, dateToCompare, interval)
    },
    isSameOrBefore(dateToCompare, interval?) {
      return isSameOrBefore(dateObj, toDate(dateToCompare), interval)
    },
    isSameOrAfter(dateToCompare, interval?) {
      return isSameOrAfter(dateObj, toDate(dateToCompare), interval)
    },
    isSame(dateToCompare, interval?) {
      return isSame(dateObj, dateToCompare, interval)
    },
    isNoon() {
      return this.hours() === 12 && this.minutes() === 0
    },
    isMidNight() {
      return this.hours() === 0 && this.minutes() === 0
    },
    isBetween(startParam, endParam) {
      const start = isDateUtil(startParam)
        ? (startParam as DateUtil).toDate()
        : (startParam as Date)
      const end = isDateUtil(endParam) ? (endParam as DateUtil).toDate() : (endParam as Date)
      return isWithinInterval(dateObj, { start: start, end: end })
    },
    isInTheRange(dateInterval: IntervalT) {
      return isWithinInterval(dateObj, dateInterval)
    },
    weekDay(index) {
      const date = getWeek(dateObj)
      return DateUtil(date).add(index, TIME.DAYS)
    },
    manupulateDate(value: number, time: TIME, action: Action) {
      dateObj = actionMap[time][action]?.(dateObj, value)
      return dateUtilObj
    },
    add(value: number, time: TIME) {
      return dateUtilObj.manupulateDate(value, time, Action.ADD)
    },
    sub(value: number, time: TIME) {
      return dateUtilObj.manupulateDate(value, time, Action.SUBTRACT)
    },
    getDatesInRange(endDate) {
      return getDatesInRange(dateUtilObj, endDate)
    },
    set(param) {
      if (param.year !== undefined) {
        dateObj = setYear(dateObj, param.year)
      }
      if (param.month !== undefined) {
        dateObj = setMonth(dateObj, param.month)
      }
      if (param.date !== undefined) {
        dateObj = setDate(dateObj, param.date)
      }
      if (param.hour !== undefined) {
        dateObj = setHours(dateObj, param.hour)
      }
      if (param.minute !== undefined) {
        dateObj = setMinutes(dateObj, param.minute)
      }
      if (param.second !== undefined) {
        dateObj = setSeconds(dateObj, param.second)
      }
      if (param.millisecond !== undefined) {
        dateObj = setMilliseconds(dateObj, param.millisecond)
      }
      return dateUtilObj
    },
    setDate(param) {
      const date = toDate(param)
      dateUtilObj.set({
        millisecond: date.getMilliseconds(),
        second: date.getSeconds(),
        minute: date.getMinutes(),
        hour: date.getHours(),
        date: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
      })
      return dateUtilObj
    },
    seconds() {
      return getSeconds(dateObj)
    },
    minutes() {
      return getMinutes(dateObj)
    },
    hours() {
      return getHours(dateObj)
    },
    day() {
      return getDay(dateObj)
    },
    date() {
      return _getDate(dateObj)
    },
    week() {
      return getWeekOfMonth(dateObj)
    },
    month() {
      return getMonth(dateObj)
    },
    year() {
      return getYear(dateObj)
    },
    diff(endDate, interval) {
      return diff(dateObj, endDate, interval)
    },
    clone() {
      return DateUtil.tz(timeZone).setDate(dateObj)
    },
    iso(param, interval) {
      switch (interval) {
        case TIME.DAYS:
          dateObj = setISODay(dateObj, param)
          break
        case TIME.WEEKS:
          dateObj = setISOWeek(dateObj, param)
          break
      }
      // case T{
      //   dateObj = setISOWeekYear(dateObj, param.month)
      // }
      return dateUtilObj
    },
    getIso(interval) {
      let val = 0
      switch (interval) {
        case TIME.DAYS:
          val = getISODay(dateObj)
          break
        case TIME.WEEKS:
          val = getISOWeek(dateObj)
          break
      }
      return val
    },
    startOf(interval) {
      return startOf(dateObj, interval)
    },
    endOf(interval) {
      return endOf(dateObj, interval)
    },
    reset(interval) {
      switch (interval) {
        case TIME.SECONDS:
          this.set({ second: 0, millisecond: 0 })
          break
        case TIME.MINUTES:
          this.set({ minute: 0, second: 0, millisecond: 0 })
          break
        case TIME.HOURS:
          this.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          break
      }
      return dateUtilObj
    },
    toString() {
      return `${dateFnsFormat(dateObj, 'EEE, dd MMM yyy HH:mm:ss')} (${timeZone})`
    },
    startOfDayToDate() {
      dateObj = startOfDay(dateObj)
      return dateUtilObj
    },
    endOfDayToDate() {
      dateObj = endOfDay(dateObj)
      return dateUtilObj
    },
    toISOString() {
      return formatISO(dateObj)
    },
  }

  return dateUtilObj
}

// common methods
const toDate = (date: DUDateT) => {
  return typeof date === 'string' || typeof date === 'number'
    ? new Date(date)
    : ((isDateUtil(date) ? (date as DateUtil).toDate() : date) as Date)
}

const getDatesInRange: DateUtilTStatic['getDatesInRange'] = (startDate, endDate) => {
  const dates: DateUtil[] = Array(diff(endDate, startDate, TIME.DAYS))
    .fill(null)
    .map((d, i) => startDate.clone().add(i, TIME.DAYS))
  dates.push(endDate)
  return dates
}

const getDateInterval = (dateParam: DUDateT, interval: string) => {
  const date = toDate(dateParam)
  switch (interval) {
    case 'seconds':
      return getSeconds(date)
    case 'minutes':
      return getMinutes(date)
    case 'hours':
      return getHours(date)
    case 'days':
      return getDayOfYear(date)
    case 'weeks':
      return getWeek(date)
    case 'months':
      return getMonth(date)
    case 'years':
      return getYear(date)
    default:
      return date.getMilliseconds()
  }
}

const tz: DateUtilTStatic['tz'] = (...args) => {
  const timezone = (args.length === 1 ? args[0] : args[1]) as unknown as string
  const date = (args.length === 1 ? undefined : args[0]) as unknown as DateT | undefined
  return DateUtil(date).tz(timezone || defaultTz())
}
const utc: DateUtilTStatic['utc'] = (date, timezone) => {
  return DateUtil(date).tz(timezone)
}
const isDateUtil: DateUtilTStatic['isDateUtil'] = (date: DUDateT) => {
  return date?.hasOwnProperty('isDateUtil')
}
const isSame: DateUtilTStatic['isSame'] = (dateObj, dateToCompare, interval) => {
  return interval
    ? (Array.isArray(interval) ? interval : [interval]).every(
        (k) => getDateInterval(dateObj, k) === getDateInterval(dateToCompare, k)
      )
    : _isEqual(toDate(dateObj), toDate(dateToCompare))
}
const isBefore: DateUtilTStatic['isBefore'] = (dateObj, dateToCompare, interval) => {
  return interval
    ? (Array.isArray(interval) ? interval : [interval]).every(
        (k) => getDateInterval(dateObj, k) < getDateInterval(dateToCompare, k)
      )
    : _isBefore(toDate(dateObj), toDate(dateToCompare))
}
const isAfter: DateUtilTStatic['isAfter'] = (dateObj, dateToCompare, interval) => {
  return interval
    ? (Array.isArray(interval) ? interval : [interval]).every(
        (k) => getDateInterval(dateObj, k) > getDateInterval(dateToCompare, k)
      )
    : _isAfter(toDate(dateObj), toDate(dateToCompare))
}
const isSameOrBefore: DateUtilTStatic['isSameOrBefore'] = (dateObj, dateToCompare, interval) => {
  return (
    isBefore(toDate(dateObj), toDate(dateToCompare), interval) ||
    isSame(toDate(dateObj), toDate(dateToCompare), interval)
  )
}
const isSameOrAfter: DateUtilTStatic['isSameOrAfter'] = (dateObj, dateToCompare, interval) => {
  return (
    isAfter(toDate(dateObj), toDate(dateToCompare), interval) ||
    isSame(toDate(dateObj), toDate(dateToCompare), interval)
  )
}
const defaultTz: DateUtilTStatic['defaultTz'] = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

const startOf: DateUtilTStatic['startOf'] = (dateObj, interval) => {
  let val: Date = new Date()
  switch (interval) {
    case TIME.MINUTES:
      val = startOfMinute(dateObj)
      break
    case TIME.HOURS:
      val = startOfHour(dateObj)
      break
    case TIME.DAYS:
      val = startOfDay(dateObj)
      break
    case TIME.WEEKS:
      val = startOfWeek(dateObj)
      break
    case TIME.MONTHS:
      val = startOfMonth(dateObj)
      break
    case TIME.YEARS:
      val = startOfYear(dateObj)
      break
  }
  return DateUtil(val)
}

const endOf: DateUtilTStatic['endOf'] = (dateObj, interval) => {
  let val: Date = new Date()
  switch (interval) {
    case TIME.MINUTES:
      val = endOfMinute(dateObj)
      break
    case TIME.HOURS:
      val = endOfHour(dateObj)
      break
    case TIME.DAYS:
      val = endOfDay(dateObj)
      break
    case TIME.WEEKS:
      val = endOfWeek(dateObj)
      break
    case TIME.MONTHS:
      val = endOfMonth(dateObj)
      break
    case TIME.YEARS:
      val = endOfYear(dateObj)
      break
  }
  return DateUtil(val)
}

const diff: DateUtilTStatic['diff'] = (startDate, endDate, interval) => {
  let val = 0
  switch (interval) {
    case TIME.SECONDS:
      val = differenceInSeconds(toDate(startDate), toDate(endDate))
      break
    case TIME.MINUTES:
      val = differenceInMinutes(toDate(startDate), toDate(endDate))
      break
    case TIME.HOURS:
      val = differenceInHours(toDate(startDate), toDate(endDate))
      break
    case TIME.DAYS:
      val = differenceInDays(toDate(startDate), toDate(endDate))
      break
    case TIME.WEEKS:
      val = differenceInWeeks(toDate(startDate), toDate(endDate))
      break
    case TIME.MONTHS:
      val = differenceInMonths(toDate(startDate), toDate(endDate))
      break
    case TIME.YEARS:
      val = differenceInYears(toDate(startDate), toDate(endDate))
      break
  }
  return val
}

const parse: DateUtilTStatic['parse'] = (dateStr, format = 'MM-dd-yyyy') => {
  return DateUtil(_parse(dateStr, format, new Date()))
}

// static members
DateUtil.tz = tz
DateUtil.utc = utc
DateUtil.isDateUtil = isDateUtil
DateUtil.getDatesInRange = getDatesInRange
DateUtil.isSame = isSame
DateUtil.isBefore = isBefore
DateUtil.isSameOrBefore = isSameOrBefore
DateUtil.isAfter = isAfter
DateUtil.isSameOrAfter = isSameOrAfter
DateUtil.startOf = startOf
DateUtil.endOf = endOf
DateUtil.diff = diff
DateUtil.parse = parse
DateUtil.defaultTz = defaultTz

const momoment = moment
eval('window._dateUtil = DateUtil')
eval('window._moment = momoment')

export { DateUtil }
